import { useLazyQuery, useMutation } from '@apollo/client/react';
import { CreateCouponDocument, GetCheckedInApiUsersDocument } from '@drycut/graphql';
import { Modal, Spinner, Stack } from '@drycut/ui-components';
import { useEffect, useState } from 'react';

import type { CouponFormData, SelectOption } from './types';

import { CouponDetailsStep } from './CouponDetailsStep';
import { CouponSuccessStep } from './CouponSuccessStep';
import { NoStylistsAvailableWarning } from './NoStylistsAvailableWarning';
import { StylistAuthStep } from './StylistAuthStep';

type Step = 'auth' | 'details' | 'success';

type Customer = {
  id: string;
  name: string;
  phoneNumber: string;
};

type Props = {
  onClose: () => void;
  open: boolean;
};

export function CreateCouponModal({ open, onClose }: Props): React.ReactElement {
  const [step, setStep] = useState<Step>('details');
  const [selectedType, setSelectedType] = useState<null | SelectOption>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [error, setError] = useState<null | string>(null);
  const [pendingFormData, setPendingFormData] = useState<CouponFormData | null>(null);
  const [couponCode, setCouponCode] = useState<null | string>(null);

  const [getCheckedInApiUsers, { data: apiUsersData, loading: apiUsersLoading }] = useLazyQuery(
    GetCheckedInApiUsersDocument,
    { fetchPolicy: 'network-only' },
  );
  const [createCoupon, { loading }] = useMutation(CreateCouponDocument);

  // Reset state when modal opens and fetch checked-in API users
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect, react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setStep('details');
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setSelectedType(null);
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setCustomer(null);
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setError(null);
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setPendingFormData(null);
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setCouponCode(null);
      getCheckedInApiUsers();
    }
  }, [open, getCheckedInApiUsers]);

  const handleTypeChange = (option: null | SelectOption) => {
    setSelectedType(option);
    setError(null);
  };

  const handleDetailsSubmit = (data: CouponFormData) => {
    setPendingFormData(data);
    setStep('auth');
  };

  const isGiftCard = selectedType?.value === 'Coupons::Goodwills::GiftCard';

  const handleAuthenticated = async (stylistId: string) => {
    if (!pendingFormData || (!customer && !isGiftCard)) return;

    try {
      setError(null);

      const result = await createCoupon({
        variables: {
          input: {
            type: selectedType?.value || '',
            reason: pendingFormData.reason,
            customerId: customer?.id,
            stylistId,
          },
        },
      });

      if (result.data?.createCoupon?.coupon) {
        setCouponCode(result.data.createCoupon.coupon.code);
        setStep('success');
      } else if (result.data?.createCoupon?.errors && result.data.createCoupon.errors.length > 0) {
        const errorMsg = result.data.createCoupon.errors.map((e) => e.message).join(', ');
        setError(errorMsg);
        setStep('details');
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Nicht-Ausstellung des Gutscheins';
      setError(errorMessage);
      setStep('details');
    }
  };

  const handleBack = () => {
    setStep('details');
  };

  const handleClose = () => {
    onClose();
  };

  // Control variables
  const apiUsers = apiUsersData?.checkedInApiUsers || [];
  const loadedWithUsers = !apiUsersLoading && apiUsers.length > 0;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={step === 'success' ? 'Gutschein erstesllt' : 'Gutschein erstellen'}
      closeButton
      overlayClose={!loading}
    >
      {apiUsersLoading && (
        <Stack className="w-80 py-16 items-center">
          <Spinner className="size-16 text-primary-600 opacity-80" />
        </Stack>
      )}

      {!apiUsersLoading && apiUsers.length === 0 && (
        <NoStylistsAvailableWarning handleClose={handleClose} loading={loading} />
      )}

      {loadedWithUsers && step === 'details' && (
        <CouponDetailsStep
          onSubmit={handleDetailsSubmit}
          onCancel={handleClose}
          loading={loading}
          selectedType={selectedType}
          onTypeChange={handleTypeChange}
          customer={customer}
          onCustomerChange={setCustomer}
          reason={pendingFormData?.reason}
          error={error}
        />
      )}

      {loadedWithUsers && step === 'auth' && (
        <StylistAuthStep
          creatingCoupon={loading}
          onAuthenticated={handleAuthenticated}
          onBack={handleBack}
          onCancel={handleClose}
          apiUsers={apiUsers}
          couponSummary={{
            customer: { name: customer?.name || '', phoneNumber: customer?.phoneNumber || '' },
            couponType: selectedType?.label || '',
            reason: pendingFormData?.reason || '',
          }}
        />
      )}

      {loadedWithUsers && step === 'success' && couponCode && (
        <CouponSuccessStep
          couponCode={couponCode}
          customer={customer?.name}
          couponType={selectedType?.label || ''}
          onClose={handleClose}
        />
      )}
    </Modal>
  );
}
